import { math, Vec3, Quat } from 'playcanvas';
import type { InputFrame } from 'playcanvas';

import { vecToAngles } from '../core/math';

type CameraFrame = InputFrame<{
    move: [number, number, number];
    rotate: [number, number, number];
}>;

const rotation = new Quat();
const avec = new Vec3();
const bvec = new Vec3();
const cvec = new Vec3();
const dvec = new Vec3();

class Camera {
    position = new Vec3();

    angles = new Vec3();

    distance = 1;

    fov = 65;

    constructor(other?: Camera) {
        if (other) {
            this.copy(other);
        }
    }

    copy(source: Camera) {
        this.position.copy(source.position);
        this.angles.copy(source.angles);
        this.distance = source.distance;
        this.fov = source.fov;
    }

    lerp(a: Camera, b: Camera, t: number) {
        a.calcFocusPoint(avec);
        b.calcFocusPoint(bvec);

        this.position.lerp(a.position, b.position, t);
        avec.lerp(avec, bvec, t).sub(this.position);

        this.distance = avec.length();

        vecToAngles(this.angles, avec.mulScalar(1.0 / this.distance));

        this.fov = math.lerp(a.fov, b.fov, t);
    }

    look(from: Vec3, to: Vec3, up?: Vec3) {
        this.position.copy(from);
        this.distance = from.distance(to);
        const dir = avec.sub2(to, from).normalize();

        if (up) {
            // 直接从 dir 和 up 构造朝向四元数，绕过 vecToAngles 的 asin/atan2 奇异问题。
            // vecToAngles 用 asin 限制 pitch ∈ [-90,90]，在垂直方向附近 yaw 振荡；
            // 阈值切换两套 yaw 公式又会在边界产生跳变。
            // 四元数从正交基构造，全空间连续无奇异。
            //
            // PlayCanvas 相机局部坐标系：forward = -Z, up = +Y, right = +X
            // back = -dir, right = cross(up, back), corrected_up = cross(back, right)
            bvec.copy(dir).mulScalar(-1);                      // back = -forward
            cvec.cross(up, bvec);                              // right = cross(up, back)
            const rightLenSq = cvec.dot(cvec);
            if (rightLenSq > 1e-8) {
                cvec.mulScalar(1 / Math.sqrt(rightLenSq));    // normalize right
                dvec.cross(bvec, cvec).normalize();           // corrected_up = cross(back, right)

                // 旋转矩阵列: [right, corrected_up, back]
                const m00 = cvec.x, m10 = cvec.y, m20 = cvec.z;
                const m01 = dvec.x, m11 = dvec.y, m21 = dvec.z;
                const m02 = bvec.x, m12 = bvec.y, m22 = bvec.z;

                // 矩阵转四元数
                const trace = m00 + m11 + m22;
                if (trace > 0) {
                    const s = Math.sqrt(trace + 1) * 2;
                    rotation.set((m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s, 0.25 * s);
                } else if (m00 > m11 && m00 > m22) {
                    const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
                    rotation.set(0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s);
                } else if (m11 > m22) {
                    const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
                    rotation.set((m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s);
                } else {
                    const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
                    rotation.set((m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s);
                }

                rotation.getEulerAngles(this.angles);
            } else {
                // up 与 dir 平行，无法构造正交基，回退到 vecToAngles
                vecToAngles(this.angles, dir);
            }
        } else {
            vecToAngles(this.angles, dir);
        }
    }

    calcFocusPoint(result: Vec3) {
        rotation.setFromEulerAngles(this.angles)
        .transformVector(Vec3.FORWARD, result)
        .mulScalar(this.distance)
        .add(this.position);
    }
}

interface CameraController {
    onEnter(camera: Camera): void;
    update(deltaTime: number, inputFrame: CameraFrame, camera: Camera): void;
    onExit(camera: Camera): void;
}

export type { CameraFrame, CameraController };

export { Camera };
